import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { checkUserApproval, resetApprovalState } from '../redux/slices/waitlistSlice';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { isCurrentUserAdmin, isAdminUser } from '../utils/adminUtils';

// Environment variable to enable/disable waitlist
const WAITLIST_ENABLED = process.env.REACT_APP_WAITLIST_ENABLED === 'true' || false;

const WaitlistGuard = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [checkingProfile, setCheckingProfile] = useState(false);
  const [hasUserProfile, setHasUserProfile] = useState(null); // null = unknown, true/false = known

  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const currentUser = useSelector(state => state.auth.currentUser);
  const isUserApproved = useSelector(state => state.waitlist.isUserApproved);
  const checkingApproval = useSelector(state => state.waitlist.checkingApproval);
  const isAdmin = isCurrentUserAdmin(currentUser);

  // Load waitlist stats on mount (always call hooks in the same order)
  // Commented out to reduce Firestore quota usage - stats are loaded by waitlist page when needed
  // useEffect(() => {
  //   if (WAITLIST_ENABLED) {
  //     dispatch(getWaitlistStats());
  //   }
  // }, [dispatch]);

  // Check user approval status when user is authenticated (always call hooks in the same order)
  useEffect(() => {
    if (WAITLIST_ENABLED && isAuthenticated && currentUser?.email && isUserApproved === null && !isAdmin) {
      dispatch(checkUserApproval(currentUser.email));
    }
  }, [dispatch, isAuthenticated, currentUser?.email, isUserApproved, isAdmin]);

  // Phase 5 Fix: Reset stale approval state when auth user changes
  useEffect(() => {
    if (WAITLIST_ENABLED && isAuthenticated && currentUser?.email) {
      // Clear stale approval state when user changes to prevent wrong approval status
      dispatch(resetApprovalState());
    }
  }, [dispatch, isAuthenticated, currentUser?.email]);

  // Determine if the authenticated user already has a profile doc (existing user)
  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!WAITLIST_ENABLED || !isAuthenticated || !currentUser?.uid) {
        setHasUserProfile(null);
        return;
      }
      if (isAdmin) {
        // Admins bypass waitlist entirely
        setHasUserProfile(true);
        setCheckingProfile(false);
        return;
      }
      setCheckingProfile(true);
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (!isMounted) return;
        setHasUserProfile(snap.exists());
      } catch (e) {
        if (!isMounted) return;
        // On error, be permissive and consider as existing to avoid blocking access
        setHasUserProfile(true);
      } finally {
        if (isMounted) setCheckingProfile(false);
      }
    };
    run();
    return () => { isMounted = false; };
  }, [isAuthenticated, currentUser?.uid, isAdmin]);

  // If waitlist is disabled, render children normally
  if (!WAITLIST_ENABLED) {
    return children;
  }

  // If we're still checking approval status or user profile, show loading
  if (!isAdmin && (checkingApproval || checkingProfile || (WAITLIST_ENABLED && isAuthenticated && hasUserProfile === null))) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh]">
        <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-green-500 border-t-transparent" role="status">
          <span className="sr-only">Checking access...</span>
        </div>
      </div>
    );
  }

  // Apply waitlist rules only to new users (no profile doc)
  if (WAITLIST_ENABLED && isAuthenticated) {
    if (isAdmin) {
      return children;
    }
    if (hasUserProfile === true) {
      // Existing user: always allow
      return children;
    }
    if (hasUserProfile === false) {
      // New user without profile: require approval to proceed
      if (isUserApproved === false) {
        return <Navigate to="/waitlist" state={{ from: location.pathname }} replace />;
      }
      if (isUserApproved === true) {
        return children;
      }
      // Unknown approval at this point should have been covered by the loading UI above
    }
  }

  // Default: render children (covers unauthenticated routes controlled by parent routing too)
  if (isAuthenticated) return children;

  // For unauthenticated users, the component will be handled by parent routing logic
  return children;
};

// Re-export for backward compatibility
export { isAdminUser };

export default WaitlistGuard;
